// src/pages/Article.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Star, Share2, ChevronRight, Check } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';

// Vite-compatible environment variables
const BASE_URL =  'https://www.motorsbay.co.uk';
const SITE_NAME = 'MotorsBay';
const TWITTER_HANDLE = '@MotorsBay';

// --- Enhanced SEO Metadata Component ---
const SeoMetadata = ({ content, contentType }) => {
  if (!content) return null;

  const isArticle = contentType === 'article';
  
  // Dynamic title generation
  const title = isArticle 
    ? `${content.title} | ${SITE_NAME}` 
    : `${content.car_model} Review - ${content.make || 'Car'} | ${SITE_NAME}`;
  
  // Dynamic description with fallback
  const description = content.excerpt || 
    (isArticle 
      ? `Read the latest automotive news: ${content.title}` 
      : `Comprehensive review of the ${content.car_model}. Rating: ${content.rating}/10`);
  
  // Handle image URL - ensure it's absolute
  const getAbsoluteImageUrl = (url) => {
    if (!url) return `${BASE_URL}/default-og-image.jpg`;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };
  
  const imageUrl = getAbsoluteImageUrl(content.image_url);
  const canonicalUrl = `${BASE_URL}/${contentType}/${content.id}`;
  
  // Generate keywords from content
  const keywords = isArticle
    ? `automotive news, car news UK, ${content.title}, MotorsBay`
    : `${content.make} ${content.car_model}, car review, ${content.car_model} rating, automotive reviews UK`;

  // Article structured data for Google
  const structuredData = isArticle ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": content.title,
    "description": description,
    "image": imageUrl,
    "datePublished": content.published_at || content.created_at,
    "dateModified": content.updated_at || content.created_at,
    "author": {
      "@type": "Organization",
      "name": SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  } : {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "Car",
      "name": content.car_model,
      "brand": {
        "@type": "Brand",
        "name": content.make
      }
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": content.rating,
      "bestRating": "10",
      "worstRating": "0"
    },
    "author": {
      "@type": "Organization",
      "name": SITE_NAME
    },
    "datePublished": content.created_at,
    "reviewBody": description
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isArticle ? 'article' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={isArticle ? content.title : content.car_model} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Article-specific OG tags */}
      {isArticle && (
        <>
          <meta property="article:published_time" content={content.published_at || content.created_at} />
          {content.updated_at && <meta property="article:modified_time" content={content.updated_at} />}
          <meta property="article:author" content={SITE_NAME} />
          <meta property="article:section" content="Automotive News" />
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />

      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content={SITE_NAME} />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

const Article = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentType, setContentType] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const { supabase } = useSupabase();

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContent = async () => {
    try {
      const { data: articleData, error: articleError } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single();

      if (articleData && !articleError) {
        setArticle(articleData);
        setContentType('article');
      } else {
        const { data: reviewData, error: reviewError } = await supabase
          .from('reviews')
          .select('*')
          .eq('id', id)
          .single();

        if (reviewData && !reviewError) {
          setReview(reviewData);
          setContentType('review');
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const title = contentType === 'article' ? article.title : `${review.car_model} Review`;
    const text = contentType === 'article' ? article.excerpt : review.excerpt;
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      } catch (err) {
        console.error('Could not copy text: ', err);
      }
    }
  };

  const ArticleSkeleton = () => (
    <div className="animate-pulse min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet><title>Loading... | {SITE_NAME}</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="h-6 w-32 bg-gray-300 rounded mb-12"></div>
        <div className="h-96 w-full bg-gray-300 rounded-2xl mb-12"></div>
        <div className="space-y-6">
          <div className="h-8 w-48 bg-gray-300 rounded"></div>
          <div className="h-6 w-3/4 bg-gray-300 rounded"></div>
          <div className="h-32 w-full bg-gray-300 rounded"></div>
        </div>
      </div>
    </div>
  );

  if (loading) return <ArticleSkeleton />;

  if (!article && !review) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <Helmet>
          <title>Content Not Found | {SITE_NAME}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="text-center px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-2xl mb-6">
            <ArrowLeft className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-3">Content Not Found</h1>
          <p className="text-gray-600 mb-8">The article or review you're looking for doesn't exist.</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-black transition-all transform hover:scale-105"
          >
            Back to Home <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const content = contentType === 'article' ? article : review;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white font-sans text-gray-900">
      <SeoMetadata content={content} contentType={contentType} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-between items-center">
          <Link
            to={contentType === 'article' ? '/news' : '/reviews'}
            className="inline-flex items-center gap-2 text-sm font-black text-red-600 hover:text-red-700 transition-all hover:gap-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {contentType === 'article' ? 'News' : 'Reviews'}
          </Link>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-2 text-sm font-black text-red-600 hover:text-red-700 transition-all hover:gap-3 bg-red-100 px-4 py-2 rounded-lg hover:bg-red-200"
          >
            {showCopied ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                Share
              </>
            )}
          </button>
        </div>
      </div>

      {content.image_url && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <img
            src={content.image_url}
            alt={contentType === 'article' ? content.title : content.car_model}
            className="w-full h-96 md:h-[500px] object-cover rounded-2xl shadow-xl"
          />
        </div>
      )}

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b-2 border-gray-200 pb-8 mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
              <Calendar className="h-4 w-4 text-red-600" />
              {format(new Date(content.created_at), 'MMMM dd, yyyy')}
            </div>
            
            {contentType === 'review' && (
              <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-full">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < content.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-black text-sm text-gray-900">{content.rating}/10</span>
              </div>
            )}
          </div>

          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            {contentType === 'article' ? content.title : `${content.car_model} Review`}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
            {content.excerpt}
          </p>
        </div>

        <div className="prose prose-lg max-w-none mb-12 text-gray-700 leading-8">
          <style>{`
            .prose h2 {
              @apply text-3xl font-black text-gray-900 mt-8 mb-4;
            }
            .prose h3 {
              @apply text-2xl font-black text-gray-900 mt-6 mb-3;
            }
            .prose p {
              @apply text-base leading-8 text-gray-700 mb-4;
            }
            .prose ul, .prose ol {
              @apply my-6 ml-6;
            }
            .prose li {
              @apply text-base text-gray-700 mb-2;
            }
            .prose strong {
              @apply font-black text-gray-900;
            }
            .prose em {
              @apply text-red-600 font-semibold;
            }
            .prose blockquote {
              @apply border-l-4 border-red-600 pl-6 py-2 italic text-gray-600 my-6;
            }
          `}</style>
          <ReactMarkdown>{content.content}</ReactMarkdown>
        </div>

        {contentType === 'review' && (
          <div className="relative rounded-2xl p-8 mb-12 overflow-hidden shadow-lg border-2 border-red-200">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-white"></div>
            <div className="relative z-10">
              <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-red-600 text-white rounded-lg">
                  ✓
                </span>
                Final Verdict
              </h3>
              <div className="flex items-center gap-6">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-8 w-8 ${
                        i < content.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <div>
                  <p className="text-3xl font-black text-gray-900">
                    {content.rating}/10
                  </p>
                  <p className="text-sm text-gray-600 font-semibold mt-1">
                    {content.rating >= 9 && 'Highly Recommended'}
                    {content.rating >= 7 && content.rating < 9 && 'Recommended'}
                    {content.rating >= 5 && content.rating < 7 && 'Good'}
                    {content.rating >= 3 && content.rating < 5 && 'Fair'}
                    {content.rating < 3 && 'Not Recommended'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="border-t-2 border-gray-200 pt-12 pb-12">
          <h3 className="text-3xl font-black text-gray-900 mb-8">
            More {contentType === 'article' ? 'News' : 'Reviews'}
          </h3>
          <Link
            to={contentType === 'article' ? '/news' : '/reviews'}
            className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black transition-all transform hover:scale-105 shadow-lg"
          >
            Browse all {contentType === 'article' ? 'news articles' : 'car reviews'}
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </article>

      <div className="relative py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black mt-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            Explore <span className="text-red-500">More Content</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            {contentType === 'article' 
              ? 'Check out our latest automotive news and stories.'
              : 'Discover detailed reviews and ratings for all car models.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to={contentType === 'article' ? '/news' : '/reviews'} 
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black transition-all transform hover:scale-105 shadow-lg shadow-red-900/30"
            >
              View All {contentType === 'article' ? 'News' : 'Reviews'} <ChevronRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border-2 border-white/30 text-white px-8 py-4 rounded-xl font-black transition-all"
            >
              Back to Home <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;